import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Zap, ShieldCheck, Wallet, Bell, ChevronDown, CheckCircle2,
  TrendingUp, TrendingDown, Vault, LogIn, LogOut,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from './ui/dropdown-menu';
import { useApp } from '../context/AppContext';
import KycModal from './modals/KycModal';
import VaultModal from './modals/VaultModal';

export default function Navbar() {
  const location = useLocation();
  const { vaultBalance, kycStatus, user, isAuthed, openAuth, logout } = useApp();
  const [kycOpen, setKycOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [tradeMode, setTradeMode] = useState('buy');

  const links = [
    { to: '/', label: 'Unlisted Markets' },
    { to: '/pipeline', label: 'Upcoming Unlisted Markets', badge: 'PIPELINE' },
    { to: '/portfolio', label: 'My Portfolio & Escrow' },
  ];
  const isActive = (to) => location.pathname === to;

  const displayName = isAuthed && user?.fullName ? user.fullName : 'Guest Investor';
  const initials = isAuthed && user?.fullName
    ? user.fullName.trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join('').toUpperCase()
    : 'GI';

  return (
    <>
      <header className="sticky top-0 z-40 bg-[rgba(10,10,12,0.85)] backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--orange)] to-[#c2410c] flex items-center justify-center shadow-lg shadow-orange-900/30">
              <Zap size={18} className="text-white fill-white" />
            </div>
            <div className="leading-none">
              <div className="flex items-center gap-1.5">
                <span className="font-display font-bold text-[17px] tracking-tight text-white">APEXFORGE</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[var(--panel-2)] text-[var(--orange)] border border-[var(--border)]">ATS</span>
              </div>
              <div className="text-[9px] tracking-[0.18em] text-[var(--muted-2)] mt-0.5 font-mono">US UNLISTED EQUITIES</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`relative px-3 py-2 text-[13px] rounded-md transition-colors flex items-center gap-2 ${
                  isActive(l.to) ? 'text-white bg-[var(--panel-2)]' : 'text-[var(--muted)] hover:text-white'
                }`}
              >
                {l.label}
                {l.badge && (
                  <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-[#2a1a3a] text-[#c084fc] border border-[#3f2a5a]">{l.badge}</span>
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              onClick={() => setKycOpen(true)}
              className={`hidden md:flex items-center gap-1.5 text-[12px] font-medium px-3 py-2 rounded-md border transition-colors ${
                kycStatus === 'verified'
                  ? 'border-[#1e5b45] text-[var(--green)] bg-[#0e2a20]'
                  : kycStatus === 'pending'
                  ? 'border-[#4a2f14] text-[var(--orange)] bg-[#2a1a0e]'
                  : kycStatus === 'rejected'
                  ? 'border-[#5a2020] text-[var(--red)] bg-[#2a1010]'
                  : 'border-[var(--border)] text-[var(--muted)] hover:text-white hover:border-[#3a3a42]'
              }`}
            >
              {kycStatus === 'verified' ? <CheckCircle2 size={14} /> : <ShieldCheck size={14} />}
              {kycStatus === 'pending' ? 'KYC PENDING' : kycStatus === 'rejected' ? 'KYC REJECTED' : 'SEC 506(c) KYC'}
            </button>
            <button
              onClick={() => setVaultOpen(true)}
              className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-md bg-[var(--panel-2)] border border-[var(--border)] text-white hover:border-[var(--orange)] transition-colors"
            >
              <Wallet size={14} className="text-[var(--orange)]" />
              VAULT <span className="font-mono text-[var(--green)]">${vaultBalance.toLocaleString()}</span>
            </button>
            <button className="w-9 h-9 rounded-md flex items-center justify-center text-[var(--muted)] hover:text-white hover:bg-[var(--panel-2)] transition-colors">
              <Bell size={17} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 pl-1 pr-1.5 py-1 rounded-md hover:bg-[var(--panel-2)] transition-colors outline-none">
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#f97316] to-[#db2777] flex items-center justify-center text-[11px] font-bold text-white">{initials}</div>
                  <ChevronDown size={14} className="text-[var(--muted)]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 bg-[var(--panel)] border-[var(--border)] text-[var(--text)] p-0">
                <div className="p-4 border-b border-[var(--border)]">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[15px] font-semibold text-white">{displayName}</span>
                    <span className="text-[12px] text-[var(--muted)]">({tradeMode === 'buy' ? 'Investor' : 'Shareholder'})</span>
                  </div>
                  <div className="text-[10.5px] font-mono text-[var(--muted-2)] mt-1">UID: {user ? user.id.slice(0, 8) : '—'}…</div>
                  <div className="flex items-center gap-2 mt-2.5">
                    <span className="text-[9px] font-mono px-2 py-1 rounded bg-[#2a1a0e] text-[var(--orange)] border border-[#4a2f14]">{tradeMode === 'buy' ? 'INVESTOR' : 'SHAREHOLDER'}</span>
                    <span className="text-[11px] text-[var(--muted)]">{user?.entityType || 'Individual'}</span>
                  </div>
                </div>

                <div className="p-3 border-b border-[var(--border)]">
                  <div className="text-[9px] font-mono tracking-[0.14em] text-[var(--muted-2)] mb-2">SWITCH TRADING MODE</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setTradeMode('buy')} className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11.5px] font-semibold transition-colors ${tradeMode === 'buy' ? 'bg-[var(--orange)] text-white' : 'bg-[var(--panel-2)] border border-[var(--border)] text-[var(--muted)] hover:text-white'}`}>
                      <TrendingUp size={13} /> Investor (Buy)
                    </button>
                    <button onClick={() => setTradeMode('sell')} className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11.5px] font-semibold transition-colors ${tradeMode === 'sell' ? 'bg-[var(--orange)] text-white' : 'bg-[var(--panel-2)] border border-[var(--border)] text-[var(--muted)] hover:text-white'}`}>
                      <TrendingDown size={13} /> Shareholder (Sell)
                    </button>
                  </div>
                </div>

                <div className="p-2">
                  <MenuRow icon={ShieldCheck} label="Accreditation & KYC Settings" onClick={() => setKycOpen(true)} />
                  <MenuRow icon={Vault} label="Institutional Crypto Vault" onClick={() => setVaultOpen(true)} />
                  {isAuthed ? (
                    <MenuRow icon={LogOut} label="Sign Out" danger onClick={() => logout()} />
                  ) : (
                    <MenuRow icon={LogIn} label="Switch Account / Sign In" onClick={() => openAuth('signin')} />
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <KycModal open={kycOpen} onClose={() => setKycOpen(false)} />
      <VaultModal open={vaultOpen} onClose={() => setVaultOpen(false)} />
    </>
  );
}

function MenuRow({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-colors ${
        danger ? 'text-[var(--red)] hover:bg-[#2a1010]' : 'text-[var(--text)] hover:bg-[var(--panel-2)]'
      }`}
    >
      <Icon size={16} className={danger ? 'text-[var(--red)]' : 'text-[var(--orange)]'} /> {label}
    </button>
  );
}
